import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import './Subscribe.css';

declare global {
  interface Window {
    createLemonSqueezy: () => void;
    LemonSqueezy: {
      Setup: (options: { eventHandler: (event: any) => void }) => void;
      Url: {
        Open: (url: string) => void;
        Close: () => void;
      };
    };
  }
}

const CHECKOUT_URLS: Record<string, string> = {
  monthly: process.env.REACT_APP_LS_MONTHLY_URL ?? '',
  annual: process.env.REACT_APP_LS_ANNUAL_URL ?? '',
};

const PLAN_LABELS: Record<string, string> = {
  monthly: 'Pro Monthly — $9.99 / month',
  annual: 'Pro Annual — $49.99 / year',
};

type State = 'loading' | 'open' | 'error';

function buildCheckoutUrl(baseUrl: string, uid: string): string {
  const successUrl = `${window.location.origin}/subscribe/success`;
  return `${baseUrl}?embed=1&checkout[custom][uid]=${encodeURIComponent(uid)}&checkout[success_url]=${encodeURIComponent(successUrl)}`;
}

const Subscribe: React.FC = () => {
  const [searchParams] = useSearchParams();
  const [state, setState] = useState<State>('loading');
  const [errorMsg, setErrorMsg] = useState('');
  const didInit = useRef(false);
  const lsReady = useRef(false);

  const uid = searchParams.get('uid') ?? '';
  const plan = searchParams.get('plan') ?? '';
  const baseUrl = CHECKOUT_URLS[plan] ?? '';

  const openCheckout = useCallback(() => {
    if (!lsReady.current) return;
    const url = buildCheckoutUrl(baseUrl, uid);
    window.LemonSqueezy.Url.Open(url);
    setState('open');
  }, [baseUrl, uid]);

  useEffect(() => {
    document.title = 'Checkout — FitMind AI';
    if (didInit.current) return;
    didInit.current = true;

    if (!uid || !['monthly', 'annual'].includes(plan) || !baseUrl) {
      setErrorMsg('Invalid checkout link. Please return to the app and try again.');
      setState('error');
      return;
    }

    const script = document.createElement('script');
    script.src = 'https://app.lemonsqueezy.com/js/lemon.js';
    script.defer = true;

    script.onload = () => {
      window.createLemonSqueezy();
      window.LemonSqueezy.Setup({
        eventHandler: (event: any) => {
          if (event.event === 'Checkout.Success') {
            window.location.href = `${window.location.origin}/subscribe/success`;
          }
        },
      });
      lsReady.current = true;
      openCheckout();
    };

    script.onerror = () => {
      setErrorMsg('Could not load the payment form. Check your connection and try again.');
      setState('error');
    };

    document.head.appendChild(script);
    return () => {
      if (document.head.contains(script)) document.head.removeChild(script);
    };
  }, [uid, plan, baseUrl, openCheckout]);

  return (
    <div className="subscribe-page">
      <header className="subscribe-header">
        <Link to="/" className="subscribe-logo">
          <img src="/logo.png" alt="FitMind AI" />
        </Link>
      </header>

      <main className="subscribe-main">
        {state === 'loading' && (
          <div className="subscribe-card">
            <div className="subscribe-icon subscribe-icon--loading">
              <div className="subscribe-spinner" />
            </div>
            {PLAN_LABELS[plan] && (
              <div className="subscribe-plan-badge">{PLAN_LABELS[plan]}</div>
            )}
            <h1>Preparing your checkout…</h1>
            <p>You'll see the secure payment window in a moment.</p>
          </div>
        )}

        {state === 'open' && (
          <div className="subscribe-card">
            <div className="subscribe-icon subscribe-icon--loading">
              <div className="subscribe-spinner" />
            </div>
            {PLAN_LABELS[plan] && (
              <div className="subscribe-plan-badge">{PLAN_LABELS[plan]}</div>
            )}
            <h1>Complete your purchase</h1>
            <p>Finish the payment in the checkout window that opened.</p>
            <button className="subscribe-btn subscribe-btn--outline" onClick={openCheckout}>
              Reopen checkout
            </button>
          </div>
        )}

        {state === 'error' && (
          <div className="subscribe-card">
            <div className="subscribe-icon subscribe-icon--error">
              <span>!</span>
            </div>
            <h1>Something went wrong</h1>
            <p>{errorMsg}</p>
            {uid && ['monthly', 'annual'].includes(plan) && baseUrl && (
              <button className="subscribe-btn subscribe-btn--primary" onClick={openCheckout}>
                Try again
              </button>
            )}
            <a className="subscribe-btn subscribe-btn--outline" href="fitmindai://paywall">
              Return to app
            </a>
          </div>
        )}
      </main>

      <footer className="subscribe-footer">
        <div className="container">
          <Link to="/">FitMind AI</Link>
          <span>·</span>
          <Link to="/privacy">Privacy Policy</Link>
          <span>·</span>
          <Link to="/terms">Terms of Service</Link>
          <span>·</span>
          <Link to="/refund-policy">Refund Policy</Link>
        </div>
      </footer>
    </div>
  );
};

export default Subscribe;
